import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material'
import MuiSelect from '@mui/material/Select'
import { PropTypes } from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { commonStyles } from '../../../styles/common.styles'
import SelectAllIcon from '@hapana/shared/web/styles/assets/svgs/check-small.svg'
import ClearAllIcon from '@hapana/shared/web/styles/assets/svgs/times.svg'
import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search-small.svg'
import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import InfoSmall from '@hapana/shared/web/styles/assets/svgs/info-small.svg'

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import AvatarComponent from '../avatar/avatar.component'
import { AVATAR_SIZE, AVATAR_TYPES } from '../avatar/avatar.constants'
import Checkbox from '../checkbox/checkbox.component'
import { CHECKBOX_TYPES } from '../checkbox/checkbox.constants'
import Button from '../button/button.component'
import { BUTTON_VARIANTS } from '../button/button.constants'
import TextField from '../text-field/text-field.component'
import Tooltip from '../tooltip/tooltip.component'
import DraggableDotsIcon from '@hapana/shared/web/styles/assets/svgs/draggable-dots.svg'

import { DROPDOWN_TYPE, DROPDWON_SEARCH_FIELD_NAME } from './dropdown.constants'
import { styles, menuItemStyles } from './dropdown.styles'

const Dropdown = ({
  name,
  label,
  size,
  labelProps = {},
  type,
  disabled,
  options,
  multiple,
  required,
  error,
  placeholder,
  optionsCategory,
  shouldSearch,
  bottomHelperText,
  fieldId,
  topLabel,
  setLabelValue,
  menuProps,
  variant,
  stylesProps,
  tooltip,
  dropdownPlaceholder,
  displayEmpty,
  showError,
  customBottomButton,
  hasDragAndDrop,
  onReorder,
  maxChips,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [reorderedOptions, setReorderedOptions] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const { control, setValue, watch, formState } = useFormContext()
  const { t } = useTranslation('common')
  const buttonRef = useRef(null)
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({
    control,
    defaultValue: multiple ? [] : '',
    rules: { required },
    name,
  })
  const inputValue = multiple && !Array.isArray(value) ? [value] : value
  const hasEmptyOption = !multiple && inputValue === '' && options?.some((o) => o.value === '')

  const fieldError = formState.errors?.[name]
  const helperTextToShow = fieldError?.message || bottomHelperText

  useEffect(() => {
    if (!searchQuery) {
      if (reorderedOptions) {
        const orderMap = new Map(reorderedOptions.map((o, i) => [o.value, i]))
        const sorted = [...options].sort((a, b) => {
          const aIdx = orderMap.get(a.value) ?? Infinity
          const bIdx = orderMap.get(b.value) ?? Infinity
          return aIdx - bIdx
        })
        return setFilteredOptions(sorted)
      }
      return setFilteredOptions(options)
    }
  }, [options, searchQuery, reorderedOptions])

  const renderList = useCallback(
    (item) => {
      switch (type) {
        case DROPDOWN_TYPE.CHECKLIST:
          return (
            <Box sx={styles.checklistContainer}>
              <Checkbox
                name={`dropdown-item-checkbox-${item.value}`}
                type={CHECKBOX_TYPES.SIMPLE}
                value={value?.includes(item.value)}
                sx={styles.menuItemCheckbox}
                shouldUnregister
                formGroupProps={{ sx: styles.checkboxFormGroup }}
              />
              <ListItemText
                primaryTypographyProps={{ sx: styles.checkboxLabel }}
                primary={item.label}
              />
            </Box>
          )

        case DROPDOWN_TYPE.ICON_LIST:
          if (item?.icon) {
            return (
              <>
                <ListItemIcon sx={styles.menuItemIcon}>
                  <SvgIcon component={item?.icon} sx={item?.style} />
                </ListItemIcon>
                <ListItemText primary={item?.label} sx={styles.menuItemText} />
              </>
            )
          }
          return <ListItemText primary={item?.label} sx={styles.menuItemText} />

        case DROPDOWN_TYPE.AVATAR:
          return (
            <Box sx={commonStyles.flexRow}>
              <Box sx={styles.menuItemAvatar}>
                <AvatarComponent
                  item={{
                    src: item.avatar,
                    alt: item.label,
                  }}
                  avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                  size={AVATAR_SIZE.SMALL}
                />
              </Box>

              <ListItemText primary={item.label} sx={styles.menuItemText} />
            </Box>
          )
        default:
          return <ListItemText primary={item.label} sx={styles.menuItemText} />
      }
    },
    [type, value]
  )

  const queriedValue = watch(`${name}${DROPDWON_SEARCH_FIELD_NAME}`)

  const handleDelete = (removeValue) => {
    const newValues = value.filter((items) => items !== removeValue)
    onChange(newValues)

    if (setLabelValue) {
      const selectedOptions = options
        .filter((option) => newValues.includes(option.value))
        .map((option) => ({
          value: option.value,
          label: option.label,
        }))
      setLabelValue(selectedOptions)
    }
  }

  const handleSelectAll = () => {
    const allValues = filteredOptions?.map((option) => option.value)
    onChange(allValues)

    if (setLabelValue) {
      const selectedOptions = filteredOptions?.map((option) => ({
        value: option.value,
        label: option.label,
      }))
      setLabelValue(selectedOptions)
    }
  }

  const handleClearAll = () => {
    onChange([])

    if (setLabelValue) {
      setLabelValue([])
    }
  }

  const setSearchValueHandler = useCallback(
    (e) => {
      setSearchQuery(e.target.value)

      const resultOptions = options?.filter(
        (option) =>
          option?.value?.toLowerCase().includes(e.target.value.toLowerCase()) ||
          option?.label?.toLowerCase().includes(e.target.value.toLowerCase())
      )
      setFilteredOptions(resultOptions)
    },
    [setFilteredOptions, options]
  )

  const clearSearchValueHandler = useCallback(() => {
    setValue(`${name}${DROPDWON_SEARCH_FIELD_NAME}`, '')
    setSearchQuery('')
  }, [name, setValue])

  const handleClose = useCallback(() => {
    setValue(`${name}${DROPDWON_SEARCH_FIELD_NAME}`, '')
    setSearchQuery('')
    if (hasDragAndDrop) setIsOpen(false)
  }, [name, setValue, hasDragAndDrop])

  const handleDraggableItemClick = (item) => {
    if (item.disabled) return
    if (item.onClick) {
      item.onClick()
      handleClose()
      return
    }
    const newValue = multiple
      ? (Array.isArray(value) ? value : [value]).includes(item.value)
        ? value.filter((v) => v !== item.value)
        : [...(Array.isArray(value) ? value : [value]), item.value]
      : item.value
    onChange(newValue)
    if (setLabelValue) {
      const selectedValues = Array.isArray(newValue) ? newValue : [newValue]
      const selectedOptions = options?.filter((opt) => selectedValues.includes(opt.value))
      setLabelValue(multiple ? selectedOptions : selectedOptions[0])
    }
    if (!multiple) {
      handleClose()
    }
  }

  const handleDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return
    if (searchQuery) return

    const reordered = Array.from(filteredOptions)
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)

    setFilteredOptions(reordered)
    setReorderedOptions(reordered)
    onReorder?.(reordered)
  }

  const selectedValue = (selected) => {
    const selectedValues = Array.isArray(selected) ? selected : [selected]
    let optionsSelected = options?.filter((option) => selectedValues.includes(option.value))
    if (optionsSelected?.length > 1 && !multiple && fieldId) {
      optionsSelected = optionsSelected?.filter((option) => option.id === fieldId)
    }
    const [singleValue] = optionsSelected

    if (type === DROPDOWN_TYPE.AVATAR && !multiple) {
      return (
        <Box sx={commonStyles.flexRow}>
          <AvatarComponent
            item={{
              src: singleValue.avatar,
              alt: singleValue.label,
            }}
            avatarType={AVATAR_TYPES.SINGLE_AVATAR}
            size={AVATAR_SIZE.SMALL}
          />
          <Typography typography="large" color="gray.80" ml={1.5}>
            {singleValue.label}
          </Typography>
        </Box>
      )
    }

    if (type === DROPDOWN_TYPE.ICON_LIST && !multiple) {
      if (singleValue?.icon) {
        return (
          <Box sx={styles.selectedIconContainer}>
            <SvgIcon component={singleValue?.icon} sx={styles.icon} />
            <Typography typography="large" color="gray.64" ml={1.5}>
              {singleValue?.label}
            </Typography>
          </Box>
        )
      }
      return (
        <Typography typography="large" color="gray.80" ml={1.5}>
          {singleValue?.label}
        </Typography>
      )
    }

    if (multiple && maxChips !== undefined && optionsSelected?.length > maxChips) {
      return (
        <Typography typography="large" color="gray.80">
          {t('dropdown.selectedCount', { count: optionsSelected.length })}
        </Typography>
      )
    }

    return multiple ? (
      <Stack direction="row" sx={styles.selectedItemStack}>
        {optionsSelected?.map((item) => (
          <Chip
            key={item.value}
            label={item.label}
            sx={styles.chipRoot}
            onDelete={() => handleDelete(item.value)}
            onMouseDown={(event) => {
              event.stopPropagation()
            }}
          />
        ))}
      </Stack>
    ) : (
      singleValue?.label
    )
  }

  const handleChange = useCallback(
    (e) => {
      const selectedOption = options.find((option) => option.value === e.target.value)

      if (setLabelValue) {
        const selectedValues = Array.isArray(e.target.value) ? e.target.value : [e.target.value]
        const selectedOptions = options?.filter((option) => selectedValues.includes(option.value))
        setLabelValue(selectedOption ?? selectedOptions)
      }
      if (selectedOption?.onClick) {
        return selectedOption.onClick()
      }

      if (Array.isArray(e.target.value) && e.target.value[0] === undefined) {
        return
      }

      onChange(e.target.value)

      if (searchQuery.length > 0 && !!queriedValue) {
        setFilteredOptions(filteredOptions)
      } else {
        setFilteredOptions(options)
      }
    },
    [filteredOptions, setLabelValue, onChange, options, queriedValue, searchQuery.length]
  )

  const bottomSheet = (
    <Paper sx={styles.bottomSheetPaperRoot}>
      <Box sx={styles.bottomSheetBtnContainer}>
        <Button
          ref={buttonRef}
          label={optionsCategory}
          type="button"
          variant={BUTTON_VARIANTS.TEXT}
          startIcon={SelectAllIcon}
          onClick={handleSelectAll}
        />
        <Button
          label={t('clearAll')}
          type="button"
          variant={BUTTON_VARIANTS.TEXT}
          startIcon={ClearAllIcon}
          onClick={handleClearAll}
        />
      </Box>
    </Paper>
  )

  const bottomWarningComponent = (
    <Box>
      <Typography typography="small" variant="mediumP" color="gray.80" mx={1}>
        {bottomHelperText}
      </Typography>
    </Box>
  )

  const ErrorComponent = () => {
    return (
      <Typography variant="small" color="error.main" mx={1} my={0.5}>
        {helperTextToShow}
      </Typography>
    )
  }

  const searchBar = (
    <Paper sx={styles.searchBarPaperRoot}>
      <TextField
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={true}
        data-testid={`dropdown-${name}-${DROPDWON_SEARCH_FIELD_NAME}`}
        name={`${name}-${DROPDWON_SEARCH_FIELD_NAME}`}
        placeholder={placeholder}
        sx={styles.searchBar}
        onChange={setSearchValueHandler}
        value={searchQuery}
        onClick={(e) => {
          if (e.key !== 'Escape') {
            e.stopPropagation()
          }
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') {
            e.stopPropagation()
          }
        }}
        InputProps={{
          startAdornment: <SvgIcon sx={styles.searchIcon} component={SearchIcon} size="small" />,
          endAdornment:
            searchQuery.length > 0 ? (
              <SvgIcon
                sx={styles.clearSearchIcon}
                component={ClearSearchIcon}
                onClick={clearSearchValueHandler}
              />
            ) : (
              ''
            ),
        }}
      />
    </Paper>
  )

  return (
    <Box sx={styles.selectRoot}>
      <FormControl
        required={required}
        error={error}
        sx={styles.formControl}
        size={size ? size : 'medium'}
      >
        {label ? (
          <InputLabel
            id={name}
            shrink={
              multiple
                ? Array.isArray(inputValue) && inputValue.length > 0
                : !!inputValue || hasEmptyOption
            }
            sx={[styles.label]}
          >
            {label}
          </InputLabel>
        ) : null}
        {topLabel && (
          <Box sx={styles.topLabelContainer}>
            <Typography
              id={name}
              variant="header4"
              color="gray.64"
              component={'label'}
              htmlFor={name}
              {...labelProps}
            >
              {topLabel}
            </Typography>
            {tooltip && (
              <Tooltip placement="top" title={tooltip}>
                <IconButton sx={styles.tooltip} disableRipple tabIndex={-1}>
                  <SvgIcon component={InfoSmall} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <MuiSelect
          displayEmpty={displayEmpty || hasEmptyOption}
          data-testid={`muiSelect-${label}-${DROPDWON_SEARCH_FIELD_NAME}`}
          MenuProps={{ autoFocus: false, ...menuProps }}
          ref={ref}
          id={name}
          labelId={label ? name : undefined}
          label={label}
          notched={
            multiple
              ? Array.isArray(inputValue) && inputValue.length > 0
              : !!inputValue || hasEmptyOption
          }
          sx={[styles.select, stylesProps?.select]}
          value={inputValue}
          onChange={handleChange}
          renderValue={(selected) => {
            const isEmpty =
              (!multiple && !selected) || (Array.isArray(selected) && selected.length === 0)

            if (isEmpty && dropdownPlaceholder) {
              return (
                <Typography variant="header4" color="gray.80">
                  {dropdownPlaceholder}
                </Typography>
              )
            }

            return selectedValue(selected)
          }}
          disabled={disabled}
          multiple={multiple}
          variant={variant}
          onBlur={onBlur}
          onClose={handleClose}
          {...(hasDragAndDrop && {
            open: isOpen,
            onOpen: () => setIsOpen(true),
          })}
        >
          {shouldSearch ? searchBar : null}
          {hasDragAndDrop ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="dropdown-droppable">
                {(droppableProvided) => (
                  <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    {filteredOptions?.map((item, index) => (
                      <Draggable key={item.value} draggableId={item.value} index={index}>
                        {(provided) => (
                          <MenuItem
                            data-testid={`muiSelect-${index}`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            disabled={item.disabled}
                            onClick={() => handleDraggableItemClick(item)}
                            sx={[
                              styles.menuItems,
                              styles.draggableMenuItem,
                              item.hasDivider && styles.hasDivider,
                              type === DROPDOWN_TYPE.CHECKLIST && menuItemStyles(),
                            ]}
                          >
                            <Box {...provided.dragHandleProps} sx={styles.dragHandle}>
                              <SvgIcon component={DraggableDotsIcon} sx={styles.dragHandleIcon} />
                            </Box>
                            <Box sx={styles.optionContainer}>{renderList(item)}</Box>
                          </MenuItem>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            filteredOptions?.map((item, index) => (
              <MenuItem
                data-testid={`muiSelect-${index}`}
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                sx={[
                  styles.menuItems,
                  item.hasDivider && styles.hasDivider,
                  type === DROPDOWN_TYPE.CHECKLIST && menuItemStyles(),
                ]}
              >
                <Box sx={styles.optionContainer}>{renderList(item)}</Box>
              </MenuItem>
            ))
          )}
          {filteredOptions?.length === 0 && (
            <MenuItem sx={styles.noResultFound}>
              <Typography typography="header3" color="gray.56">
                {t('noResultFound')}
              </Typography>
            </MenuItem>
          )}
          {multiple && bottomSheet}
          {customBottomButton && customBottomButton}
        </MuiSelect>
        {showError && <ErrorComponent />}
      </FormControl>
      {bottomHelperText && bottomWarningComponent}
    </Box>
  )
}

Dropdown.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  size: PropTypes.string,
  labelProps: PropTypes.object,
  type: PropTypes.oneOf(Object.values(DROPDOWN_TYPE)),
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  required: PropTypes.bool,
  error: PropTypes.bool,
  placeholder: PropTypes.string,
  optionsCategory: PropTypes.string,
  shouldSearch: PropTypes.bool,
  bottomHelperText: PropTypes.string,
  fieldId: PropTypes.string,
  topLabel: PropTypes.string,
  setLabelValue: PropTypes.func,
  menuProps: PropTypes.object,
  variant: PropTypes.string,
  stylesProps: PropTypes.object,
  tooltip: PropTypes.string,
  dropdownPlaceholder: PropTypes.string,
  displayEmpty: PropTypes.bool,
  showError: PropTypes.bool,
  customBottomButton: PropTypes.node,
  hasDragAndDrop: PropTypes.bool,
  onReorder: PropTypes.func,
  maxChips: PropTypes.number,
}

Dropdown.defaultProps = {
  name: '',
  label: '',
  size: '',
  labelProps: {},
  type: DROPDOWN_TYPE.DEFAULT,
  disabled: false,
  multiple: false,
  required: false,
  error: false,
  optionsCategory: 'Select all options',
  shouldSearch: false,
  bottomHelperText: '',
  fieldId: '',
  variant: 'outlined',
  displayEmpty: false,
  showError: false,
  hasDragAndDrop: false,
}

export default React.memo(Dropdown)
